import { useAuthActions } from "@convex-dev/auth/react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { Redirect, Tabs, useRouter } from "expo-router";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import {
  Animated,
  Easing,
  Image,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import AccountActiveIcon from "@/assets/svgs/account-active-icon";
import AccountInactiveIcon from "@/assets/svgs/account-inactive-icon";
import ChatActiveIcon from "@/assets/svgs/chat-active-icon";
import ChatInactiveIcon from "@/assets/svgs/chat-inactive-icon";
import EarningsActiveIcon from "@/assets/svgs/earnings-active-icon";
import EarningsInactiveIcon from "@/assets/svgs/earnings-inactive-icon";
import FindTutorActiveIcon from "@/assets/svgs/find-tutor-active-icon";
import FindTutorInactiveIcon from "@/assets/svgs/find-tutor-inactive-icon";
import HomeActiveIcon from "@/assets/svgs/home-active-icon";
import HomeInactiveIcon from "@/assets/svgs/home-inactive-icon";
import LessonScheduleActiveIcon from "@/assets/svgs/lesson-schedule-active-icon";
import LessonScheduleInactiveIcon from "@/assets/svgs/lesson-schedule-inactive-icon";
import ProfileActiveIcon from "@/assets/svgs/profile-active-icon";
import ProfileInactiveIcon from "@/assets/svgs/profile-inactive-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import SubjectReviewIcon from "@/assets/svgs/subject-review-icon";
import ShareMoodModal from "@/elements/components/modals/share-mood-modal";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Status } from "@/elements/components/ui/status";
import { api, auth } from "@/elements/components/utils/convex-api";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";

function RedirectParentToOnboarding() {
  const { data: parentProfile, isSuccess } = useQuery(
    convexQuery(api.parent.home.queries.getProfile, {}),
  );

  if (!isSuccess) {
    return <></>;
  }

  const onboardingSteps = parentProfile?.onboardingStepsCompleted ?? [];
  const childDetailsStepCompleted = onboardingSteps.includes("childDetails");
  const addChildrenStepCompleted = onboardingSteps.includes("addChildren");
  const legalStepCompleted = onboardingSteps.includes("legal");

  if (!parentProfile) {
    return <Redirect href={"/(app)/onboarding/(parent)/add-child"} />;
  } else if (!childDetailsStepCompleted) {
    return <Redirect href={"/(app)/onboarding/(parent)/add-child"} />;
  } else if (childDetailsStepCompleted && !addChildrenStepCompleted) {
    return <Redirect href={"/(app)/onboarding/(parent)/manage-children"} />;
  } else if (
    addChildrenStepCompleted &&
    childDetailsStepCompleted &&
    !legalStepCompleted
  ) {
    return <Redirect href={"/(app)/onboarding/(parent)/parent-legal"} />;
  }

  return <></>;
}

function RedirectTutorToOnboarding() {
  const { data: tutorProfile, isSuccess } = useQuery(
    convexQuery(api.tutor.queries.getProfile, {}),
  );

  if (!isSuccess) {
    return <></>;
  }

  const onboardingSteps = tutorProfile?.onboardingStepsCompleted ?? [];
  const teachingExperienceStepCompleted = onboardingSteps.includes("details");
  const legalStepCompleted = onboardingSteps.includes("legal");
  const isRejectedOrPending =
    !!tutorProfile?.rejectedAt || !tutorProfile?.approvedAt;

  if (!tutorProfile) {
    return <Redirect href={"/(app)/onboarding/(tutor)/teaching-experience"} />;
  } else if (!teachingExperienceStepCompleted) {
    return <Redirect href={"/(app)/onboarding/(tutor)/teaching-experience"} />;
  } else if (teachingExperienceStepCompleted && !legalStepCompleted) {
    return <Redirect href={"/(app)/onboarding/(tutor)/tutor-legal"} />;
  } else if (isRejectedOrPending) {
    return <Redirect href={"/(app)/onboarding/(tutor)/tutor-status"} />;
  }

  return <></>;
}

function HomeLayout() {
  const router = useRouter();
  const { signOut } = useAuthActions();

  const { data: currentUser, isSuccess: isCurrentUserSuccess } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );

  const persona = currentUser?.persona;

  const { mutateAsync: updateUser } = useMutation({
    mutationFn: useConvexMutation(api.account.mutations.updateUser),
  });

  const { data: pendingMoodReport } = useQuery(
    convexQuery(api.student.home.queries.getPendingMoodReport, {}),
  );

  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  function BounceWrapper({ children }: { children: React.ReactNode }) {
    const scale = useRef(new Animated.Value(1)).current;

    useEffect(() => {
      const pulse = Animated.sequence([
        Animated.timing(scale, {
          toValue: 1.15,
          duration: 150,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: 150,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1.1,
          duration: 120,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: 120,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]);

      const loop = Animated.loop(
        Animated.sequence([pulse, Animated.delay(2000)]),
      );

      loop.start();
      return () => loop.stop();
    }, [scale]);

    return (
      <Animated.View style={{ transform: [{ scale }] }}>
        {children}
      </Animated.View>
    );
  }

  useEffect(() => {
    (async () => {
      if (isCurrentUserSuccess) {
        if (!persona) {
          const savedPersona = await AsyncStorage.getItem("persona");
          if (savedPersona) {
            await updateUser({
              context: savedPersona as "tutor" | "parent" | "student",
            });
          } else {
            signOut();
          }
        }
        await AsyncStorage.removeItem("persona");
      }
    })();
  }, [persona, updateUser, isCurrentUserSuccess, signOut]);

  const handleWellbeingCheckInComplete = useCallback(
    (lessonReportId: Id<"lessonReports">) => {
      router.push({
        pathname: "/(app)/lesson-history/[lessonReportId]",
        params: { lessonReportId, tab: "well-being" },
      });
    },
    [router],
  );

  const hasPendingWellbeingCheckIn = !!pendingMoodReport?._id;

  if (!persona) {
    return (
      <View className="bg-teachLoop-creamDark flex-1 px-5">
        <Status className="justify-center">
          <Status.Illustrator />
          <View className="h-[56]" />
          <Status.Title>Welcome to Teachloop!</Status.Title>
          <View className="h-[16]" />
          <Status.SubTitle>Loading your profile..</Status.SubTitle>
        </Status>
      </View>
    );
  }

  return (
    <>
      {persona === "parent" && <RedirectParentToOnboarding />}
      {persona === "tutor" && <RedirectTutorToOnboarding />}

      {persona === "student" && (
        <ShareMoodModal
          pendingMoodReport={pendingMoodReport}
          isVisible={hasPendingWellbeingCheckIn}
          onComplete={handleWellbeingCheckInComplete}
        />
      )}

      <Tabs
        screenOptions={{
          headerShown: false,
          tabBarLabel: () => <></>,
          tabBarStyle: {
            height: 65 + insets.bottom,
            width,
            alignSelf: "center",
            paddingTop: 8,
            paddingHorizontal: (width - responsiveWidth) / 2,
          },
        }}
        initialRouteName={undefined}
        backBehavior="fullHistory"
      >
        <Tabs.Screen
          name="index"
          options={{
            tabBarIcon: ({ focused }) =>
              focused ? (
                <HomeActiveIcon pathClassName="fill-black" />
              ) : (
                <HomeInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton: (props) => {
              const { children, onPress } = props;

              return (
                <Pressable
                  onPress={onPress}
                  className="flex-1 justify-center items-center gap-0.5"
                >
                  {children}
                  <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                    Home
                  </Text>
                </Pressable>
              );
            },
          }}
        />
        <Tabs.Screen
          name="lesson-schedule"
          options={{
            tabBarIcon: ({ focused }) =>
              focused ? (
                <LessonScheduleActiveIcon pathClassName="fill-black" />
              ) : (
                <LessonScheduleInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton: (props) => {
              const { children, onPress } = props;

              return (
                <Pressable
                  onPress={onPress}
                  className="flex-1 justify-center items-center gap-0.5"
                >
                  {children}
                  <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                    Schedule
                  </Text>
                </Pressable>
              );
            },
          }}
        />
        <Tabs.Screen
          name="find-tutor"
          options={{
            href:
              persona === "tutor" || persona === "student" ? null : undefined,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <FindTutorActiveIcon pathClassName="fill-black" />
              ) : (
                <FindTutorInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton:
              persona === "tutor" || persona === "student"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                        testID="tab-find-tutor-button"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          Tutors
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="earnings"
          options={{
            href: persona !== "tutor" ? null : undefined,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <EarningsActiveIcon pathClassName="fill-black" />
              ) : (
                <EarningsInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton:
              persona !== "tutor"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          Earnings
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="lesson-history"
          options={{
            href: persona !== "student" ? null : undefined,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <SubjectReviewIcon
                  pathClassName="fill-black"
                  className="size-7"
                />
              ) : (
                <SubjectIcon pathClassName="stroke-black" className="size-7" />
              ),
            tabBarButton:
              persona !== "student"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text
                          className="font-geologica-regular font-normal text-xs text-black leading-[24px]"
                          numberOfLines={1}
                        >
                          Lesson history
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="chat"
          options={{
            href: persona === "student" ? null : undefined,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <ChatActiveIcon pathClassName="fill-black" className="size-7" />
              ) : (
                <ChatInactiveIcon
                  pathClassName="stroke-black"
                  className="size-7"
                />
              ),
            tabBarButton:
              persona === "student"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          Chat
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="account"
          options={{
            href: persona === "student" ? null : undefined,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <AccountActiveIcon pathClassName="stroke-white" />
              ) : (
                <AccountInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton:
              persona === "student"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          Account
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="profile"
          options={{
            href: persona === "student" ? undefined : null,
            tabBarIcon: ({ focused }) =>
              focused ? (
                <ProfileActiveIcon pathClassName="fill-black" />
              ) : (
                <ProfileInactiveIcon pathClassName="stroke-black" />
              ),
            tabBarButton:
              persona !== "student"
                ? undefined
                : (props) => {
                    const { children, onPress } = props;

                    return (
                      <Pressable
                        onPress={onPress}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          Profile
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />

        <Tabs.Screen
          name="mood"
          options={{
            href:
              persona === "student" && !!pendingMoodReport?._id
                ? undefined
                : null,
            tabBarIcon: () => (
              <BounceWrapper>
                <Image
                  source={require("@/assets/images/happy.png")}
                  style={{ width: 29, height: 29 }}
                />
              </BounceWrapper>
            ),
            tabBarButton:
              persona !== "student" || !pendingMoodReport?._id
                ? undefined
                : ({ children }) => {
                    return (
                      <Pressable
                        onPress={() => router.push("/home/mood")}
                        className="flex-1 justify-center items-center gap-0.5"
                      >
                        {children}
                        <Text className="font-geologica-regular font-normal text-xs text-black leading-[24px]">
                          New Report
                        </Text>
                      </Pressable>
                    );
                  },
          }}
        />
      </Tabs>
    </>
  );
}

function StudentRedirect() {
  const { signOut } = useAuthActions();
  const { data: studentProfile, isSuccess: isStudentProfileSuccess } = useQuery(
    {
      ...convexQuery(api.student.auth.queries.getPreCreatedStudentProfile, {}),
    },
  );
  const studentProfileExists = !!studentProfile?.data;
  const {
    data: existingTutorOrParentProfile,
    isSuccess: isExistingTutorOrParentProfileSuccess,
  } = useQuery({
    ...convexQuery(
      api.student.auth.queries.getExistingTutorOrParentProfile,
      {},
    ),
    enabled: studentProfileExists,
  });
  const existingTutorOrParentProfileExists =
    !!existingTutorOrParentProfile?.errorInfo;

  if (!isStudentProfileSuccess) {
    return <></>;
  }

  if (!studentProfileExists) {
    return (
      <View className="bg-teachLoop-creamDark flex-1">
        <AppBar>
          <AppBar.Icon onPress={() => signOut()} />
          <AppBar.Title>Sign Out</AppBar.Title>
        </AppBar>
        <Status className="justify-center px-5">
          <Status.Illustrator />
          <View className="h-[56]" />
          <Status.Title>{studentProfile?.errorInfo?.title}</Status.Title>
          <View className="h-[16]" />
          <Status.SubTitle>
            {studentProfile?.errorInfo?.description}
          </Status.SubTitle>
        </Status>
      </View>
    );
  }

  if (!isExistingTutorOrParentProfileSuccess) {
    return <></>;
  }

  if (existingTutorOrParentProfileExists) {
    return (
      <View className="bg-teachLoop-creamDark flex-1">
        <AppBar>
          <AppBar.Icon onPress={() => signOut()} />
          <AppBar.Title>Sign Out</AppBar.Title>
        </AppBar>
        <Status className="justify-center px-5">
          <Status.Illustrator />
          <View className="h-[56]" />
          <Status.Title>
            {existingTutorOrParentProfile?.errorInfo?.title}
          </Status.Title>
          <View className="h-[16]" />
          <Status.SubTitle>
            {existingTutorOrParentProfile?.errorInfo?.description}
          </Status.SubTitle>
        </Status>
      </View>
    );
  }

  return <HomeLayout />;
}

function HomeLayoutWrapper() {
  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {}),
  );
  const { data: personaFromStorage, isSuccess: isPersonaFromStorageSuccess } =
    useQuery({
      queryKey: ["check-persona-from-async-storage"],
      queryFn: async () => {
        const persona = await AsyncStorage.getItem("persona");
        return persona;
      },
      enabled: isSuccess,
    });

  if (!isAuthenticated && isSuccess) {
    return <Redirect href={"/(auth)/sign-in-options"} />;
  }

  if (!isPersonaFromStorageSuccess || !isSuccess) {
    return <></>;
  }

  if (personaFromStorage === "student") {
    return <StudentRedirect />;
  }

  return <HomeLayout />;
}

export default HomeLayoutWrapper;
